//new
import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Card,
  Row,
  Col,
  CardTitle,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Table,
  Alert,
  CardFooter,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import { useSelector } from 'react-redux';
import { BlobProvider } from '@react-pdf/renderer';
import { formatDate } from '../../constants/DateFormatter';
import Select from 'react-select';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

const StudentList = ({ systemData }) => {
  const navigate = useNavigate();
  const tableRef = useRef();

  const [errorMessage, setErrorMessage] = useState({});
  const [admissionList, setAdmissionList] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const sessionData = useSelector((state) => state.sessionState);
  const { user } = useSelector((state) => state.authState || {});
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [search, setSearch] = useState('');
  const [curr_sec, setcurr_sec] = useState('');
  const [curr_class, setcurr_class] = useState('');

  const [selectedClassId, setSelectedClassId] = useState(''); // id string
  const [religionName, setReligionName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [acClose, setAcClose] = useState('NO');
  const [tcIssued, setTcIssued] = useState('NO');
  const [gender, setGender] = useState('');
  const [feeGroup, setFeeGroup] = useState(0);

  const [religionList, setReligionList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [Fee_Group_data, setFee_Group_data] = useState([]);

  const optionsGender = [
    { label: 'Select', value: 'Select' },
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Other', value: 'OTHER' },
  ];

  const onHandleAddAdmission = () => {
    navigate('/addAdmission');
  };

  const exportToExcel = (
    data,
    fileName = 'StudentList.xlsx'
  ) => {

    const selectedClassName = classOptions.find(
      (o) => String(o.value) === String(selectedClassId)
    )?.label || '';

    const rows = [

      // =====================================
      // TOP MERGED TITLE ROW
      // =====================================

      [selectedClassName || 'STUDENT LIST'],

      // =====================================
      // HEADER ROW
      // =====================================

      [
        'S.No.',
        'Admission No.',
        'D.O.A.',
        'Student Name',
        'Aadhar No.',
        'PEN ID',
        'APAAR ID',
        'Gender',
        'D.O.B.',
        'Father Name',
        'Mother Name',
        'Address',
        'Religion',
        'Category',
      ],

      // =====================================
      // DATA ROWS
      // =====================================

      ...data.map((item, index) => [

        index + 1,

        item?.admission?.admit_no || '',

        item?.admission?.adm_date
          ? formatDate(item.admission.adm_date)
          : '',

        item?.admission?.stud_name || '',

        item?.admission?.addhar_no || '',

        item?.admission?.pen_id || '',

        item?.admission?.apaar_id || '',

        item?.admission?.sex_mf || '',

        item?.admission?.d_o_b
          ? formatDate(item.admission.d_o_b)
          : '',

        item?.admission?.f_name || '',

        item?.admission?.m_name || '',

        `${item?.admission?.sadd1 || ''}
       ${item?.admission?.sadd2 || ''}
       ${item?.admission?.sadd3 || ''}`
          .replace(/\s+/g, ' ')
          .trim(),

        item?.admission?.religion_name || '',

        item?.admission?.category_class || '',
      ]),
    ];

    // =====================================
    // CREATE SHEET
    // =====================================

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // =====================================
    // MERGE TOP TITLE ROW
    // =====================================

    ws['!merges'] = [
      {
        s: { r: 0, c: 0 },   // start
        e: { r: 0, c: 13 },  // end
      },
    ];

    // =====================================
    // TITLE STYLE
    // =====================================

    const titleCell = 'A1';

    if (ws[titleCell]) {

      ws[titleCell].s = {

        font: {
          bold: true,
          sz: 16,
        },

        alignment: {
          horizontal: 'center',
          vertical: 'center',
        },
      };
    }

    // =====================================
    // HEADER STYLE
    // Header row = row index 1
    // =====================================

    for (let C = 0; C <= 13; C++) {

      const cellAddress =
        XLSX.utils.encode_cell({
          r: 1,
          c: C,
        });

      if (ws[cellAddress]) {

        ws[cellAddress].s = {

          font: {
            bold: true,
          },

          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
        };
      }
    }

    // =====================================
    // COLUMN WIDTHS
    // =====================================

    ws['!cols'] = [
      { wch: 8 },
      { wch: 15 },
      { wch: 14 },
      { wch: 25 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 10 },
      { wch: 14 },
      { wch: 25 },
      { wch: 25 },
      { wch: 40 },
      { wch: 18 },
      { wch: 18 },
    ];

    // =====================================
    // ROW HEIGHTS
    // =====================================

    ws['!rows'] = [
      { hpt: 28 }, // title row
      { hpt: 24 }, // header row
    ];

    // =====================================
    // WORKBOOK
    // =====================================

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      'Student List'
    );

    // =====================================
    // EXPORT
    // =====================================

    const excelBuffer = XLSX.write(
      wb,
      {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      }
    );

    const dataBlob = new Blob(
      [excelBuffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    );

    saveAs(dataBlob, fileName);
  };

  const onHandleEditParty = (id) => {
    let findArrayData = admissionList.find((e) => e.id === id);
    navigate('/addAdmission', { state: { ...findArrayData } });
  };

  const fetchData = async () => {
    try {

      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const queryParams = new URLSearchParams();

      if (vendorId) queryParams.append('vendorId', vendorId);
      if (branchId) queryParams.append('branchId', branchId);

      // queryParams.append("session", sessionData.session_year)
      if (search) queryParams.append('search', search);
      // if (curr_class) queryParams.append('curr_class', curr_class);
      if (selectedClassId) queryParams.append('classId', String(selectedClassId));

      //if (curr_sec) queryParams.append('curr_sec', curr_sec);
      if (religionName) queryParams.append('religionName', religionName);
      if (categoryName) queryParams.append('categoryName', categoryName);
      if (gender) queryParams.append('gender', gender);
      if (acClose) queryParams.append('acClose', acClose);
      if (tcIssued) queryParams.append('tcIssued', tcIssued);
      if (feeGroup) queryParams.append('feeGroup', feeGroup);

      const response = await apiService.get(
        `api/Enrollment/GetAllEnrollment?${queryParams.toString()}`,
      );
      console.log(response)
      if (response.status === true) {
        setAdmissionList(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });
    if (canLoad) {
      fetchData();
      fetchClasses();
      GetFee_GroupData();
    }
  }, [search, selectedClassId, religionName,
    categoryName,
    gender,
    acClose,
    tcIssued,
    feeGroup, selectionData.vendor_id, selectionData.branch_id]);

  useEffect(() => {
    // fetchData();
    // fetchClasses();
    // GetFee_GroupData();
    Get_All_Multi_Purpose_Type_Master();
  }, [
    // search,
    // curr_class,
    // curr_sec,
    // religionName,
    // categoryName,
    // gender,
    // acClose,
    // tcIssued,
    // feeGroup,
  ]);

  const GetFee_GroupData = async () => {
    try {

      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      let config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };

      const response = await apiService.get(
        `api/FeeGroupMaster/GetFeeGroup?vendorId=${vendorId}&branchId=${branchId}`,
        config,
      );

      if (response.response && response.response.data.errors) {
        alert(response.response?.data?.errors);
      }

      if (response.status === true) {
        setFee_Group_data(response.data);
      } else {
        console.log(response.message);
      }
    } catch (error) {
      console.log(error);
      alert('SomeThing went wrong');
    }
  };

  const Get_All_Multi_Purpose_Type_Master = async () => {
    try {
      let config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };
      const response = await apiService.get(
        `api/Multi_Purpose_Type_Master/GetMulti_Purpose_Type_Master?filter=Religion`,
        config,
      );

      if (response.response && response.response.data.errors) {
        alert(response.response?.data?.errors);
      }
      if (response.status === true) {
        setReligionList(response.data);
      } else {
        alert(response.message);
      }

      const response2 = await apiService.get(
        `api/Multi_Purpose_Type_Master/GetMulti_Purpose_Type_Master?filter=Category_Financial`,
        config,
      );

      if (response2.response && response2.response.data.errors) {
        alert(response2.response?.data?.errors);
      }
      if (response2.status === true) {
        setCategoryList(response2.data);
      } else {
        alert(response2.message);
      }
    } catch (error) {
      console.log(error);
      alert('SomeThing went wrong!!');
    }
  };

  const fetchClasses = async (searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';
      // if (!vendorId || !branchId) {
      //   setClassOptions([]);
      //   return;
      // }

      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const raw = res?.data || [];
        const opts = (raw || [])
          .map((x) => ({
            value: String(x.id),
            label: String(
              x.class_display_name ??
              x.class_Name ??
              x.className ??
              x.name ??
              'No Name',
            ),
          }))
          .filter((o) => o.value && o.value !== '0');

        setClassOptions(opts);
      } else {
        setClassOptions([]);
      }
    } catch (err) {
      console.error(err);
      setClassOptions([]);
    }
  };


  const handleStudentListPrint = () => {
    const printContent = tableRef.current.innerHTML;
    const newWindow = window.open('', '_blank');

    const selectedClassName = classOptions.find(
      (o) => String(o.value) === String(selectedClassId)
    ).label || '';


    newWindow.document.write(`
      <html>
        <head>
          <title>Print Table</title>
          <style>
            table {  width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid black;
    border-radius: 10px;
    font-family: Arial, sans-serif;
    font-weight: normal;
    overflow: hidden; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #d5f3f2; }
              @media print {
                .no-print { display: none; }
                 .col-normal { display: none; }
                 .col-print { display: table-cell !important; }
              }
          </style>
        </head>
        <body>
          <h2 style="text-align:center;color:green">${systemData.school_name}</h2>
          <h3 style="text-align:center;color:blue">Student Register for ${selectedClassName} </h3>
          <p style="text-align:end;">Session :- ${sessionData.session_year}</p>
          ${printContent}
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() { window.close(); };
                setTimeout(() => { window.close(); }, 100);
              };
            </script>
        </body>
      </html>
    `);

    newWindow.document.close();
  };

  const clearFilters = () => {
    setSearch('');
    setcurr_sec('');
    setcurr_class('');
    setReligionName('');
    setCategoryName('');
    setAcClose('');
    setTcIssued('');
    setGender('');
    setFeeGroup(0);
  };

  return (
    <>
      <div className='text-right mb-2'>
        <Button onClick={handleStudentListPrint} className='btn btn-danger'>
          <i className='bi bi-printer mr-2' /> Print List
        </Button>
        &nbsp;
        <Button
          color='success'
          onClick={() => exportToExcel(admissionList, 'StudentList.xlsx')}
        >
          <i className='bi bi-file-earmark-spreadsheet' /> Export to Excel
        </Button>
        &nbsp;
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box height-100-p pd-20 mb-20'>
              {/* Header */}
              <div className='border-bottom d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
                {/* Title */}
                <div className='mb-2'>
                  <h4 className='fw-bold text-primary mb-1'>
                    <i className='bi bi-people mr-2'></i> Student List
                  </h4>
                  <small className='text-muted'>
                    Showing <span className='fw-semibold'>{admissionList.length}</span>{' '}
                    records, further filters can be applied to narrow down the results
                  </small>
                </div>
      
                {/* Search */}
                <div className='position-relative w-50 w-md-50'>
                  <input
              type="text"
              placeholder="Search by name, admission no..."
              onChange={(e) => setSearch(e.target.value)}
                    className='form-control ps-5 rounded-pill'
                  />
                </div>
              </div>

        <CardBody className="pb-2">
         

          {/* =============================================== */}
          {/* ROW 1 — COURSE / SECTION / GENDER / RELIGION   */}
          {/* =============================================== */}

          <div className="row mb-3">

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Course / Class
              </label>
              <Select
                options={classOptions}
                value={
                  classOptions.find(
                    (o) => String(o.value) === String(selectedClassId)
                  ) || null
                }
                onChange={(opt) =>
                  setSelectedClassId(opt ? String(opt.value) : '')
                }
                onInputChange={(val, meta) => {
                  if (meta.action === 'input-change') fetchClasses(val);
                }}
                isClearable
                isSearchable
                placeholder="Type a course..."
              />
            </div>

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Section
              </label>
              <select
                name="curr_sec"
                value={curr_sec}
                onChange={(e) => setcurr_sec(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All Sections —</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Gender
              </label>
              <select
                name="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="form-control form-control-sm"
              >
                {optionsGender.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-3">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Religion
              </label>
              <select
                name="religionName"
                value={religionName}
                onChange={(e) => setReligionName(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All Religions —</option>
                {religionList.map((option) => (
                  <option
                    key={option.multi_purpose_type}
                    value={option.multi_purpose_type}
                  >
                    {option.multi_purpose_type}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* =============================================== */}
          {/* ROW 2 — CATEGORY / A/C TYPE / TC / FEE GROUP   */}
          {/* =============================================== */}

          <div className="row mb-3">

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Category
              </label>
              <select
                name="categoryName"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All Categories —</option>
                {categoryList.map((option) => (
                  <option
                    key={option.multi_purpose_type}
                    value={option.multi_purpose_type}
                  >
                    {option.multi_purpose_type}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                A/C Type
              </label>
              <select
                name="ac"
                value={acClose}
                onChange={(e) => setAcClose(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All —</option>
                <option value="YES">Closed</option>
                <option value="NO">Open</option>
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                TC Issued
              </label>
              <select
                name="tcIssued"
                value={tcIssued}
                onChange={(e) => setTcIssued(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All —</option>
                <option value="YES">Yes</option>
                <option value="NO">No</option>
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-3">
              <label className="col-form-label-sm font-weight-bold text-muted text-uppercase">
                Fee Group
              </label>
              <select
                name="FeeGroup"
                value={feeGroup}
                onChange={(e) => setFeeGroup(e.target.value)}
                className="form-control form-control-sm"
              >
                <option value="">— All Groups —</option>
                {Fee_Group_data.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.fee_group_name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* =============================================== */}
          {/* ROW 3 — CLEAR FILTERS                         */}
          {/* =============================================== */}

          <div className="row mb-2">
            <div className="col-12 col-md-3 ml-auto">
              <Button
                color="outline-secondary"
                size="sm"
                block
                onClick={clearFilters}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-x-circle mr-2"></i>
                Clear All Filters
              </Button>
            </div>
          </div>


          <div
            ref={tableRef}
            className='table-responsive'
            style={{
              overflowX: 'auto',
              maxWidth: '150vh',
              whiteSpace: 'nowrap',
            }}
          >
            <Table hover striped responsive>
              <thead>
                <tr>
                  <th>Sno.</th>
                  <th>Adm. No.</th>
                  <th>D.O.A</th>
                  <th>Student Name</th>
                  <th>Aadhar no.</th>
                  <th>PEN ID</th>
                  <th>APAAR ID</th>
                  <th>Gender</th>
                  <th>D.O.B</th>
                  <th>Father Name</th>
                  <th>Mother Name</th>
                  <th>Address</th>
                  <th>Religion</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {admissionList.length ? (
                  admissionList.map((item, index) => (
                    <tr key={index}>
                      <th scope='row'>{index + 1}</th>
                      <td>{item.admit_no}</td>
                      <td style={{ whiteSpace: 'nowrap', minWidth: '70px' }}>
                        {formatDate(item.admission.adm_date)}
                      </td>
                      <td>{item.admission.stud_name}</td>
                      <td>{item.admission.addhar_no}</td>
                      <td>{item.admission.pen_id}</td>
                      <td>{item.admission.apaar_id}</td>
                      <td>{item.admission.sex_mf}</td>
                      <td style={{ whiteSpace: 'nowrap', minWidth: '70px' }}>
                        {formatDate(item.admission.d_o_b)}
                      </td>
                      <td>{item.admission.f_name}</td>
                      <td>{item.admission.m_name}</td>
                      <td>
                        {item.admission.sadd1},{item.admission.sadd2}
                      </td>
                      <td>{item.admission.religion_name}</td>
                      <td>{item.admission.category_class}</td>
                    </tr>
                  ))
                ) : (
                  <>
                    <tr>
                      <td className='text-center' colSpan={14}>
                        No data available
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>

        <CardFooter className='text-right'>
          (Showing latest 10 entries by default)
        </CardFooter>
      </Card>
    </>
  );
};

export default StudentList;